import Papa from "papaparse";
import axios from "axios";
import { getConfigs, getSignedUrl, trackUpload } from "../../../api/CsvUpload";
import { validateCSV } from "../../../api/ValidateCSV";

const MAX_FILE_SIZE = 100 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [".csv", "text/csv"];

export const loadConfigurations = async (region) => {
  const configsData = await getConfigs(region);
  return configsData || [];
};

export const validateFile = (file) => {
  if (!file) return { isValid: false, error: "No file provided" };
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size exceeds 100MB limit. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
    };
  }
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
  if (!ALLOWED_FILE_TYPES.includes(fileExtension) && !ALLOWED_FILE_TYPES.includes(file.type)) {
    return { isValid: false, error: "Please upload a valid CSV file" };
  }

  return { isValid: true, error: null };
};

export const parseCsv = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          reject(new Error(`CSV parsing errors: ${results.errors[0].message}`));
          return;
        }
        const validData = results.data.filter((row) =>
          Object.values(row).some((value) => value !== null && value !== "")
        );
        if (validData.length === 0) {
          reject(new Error("CSV file contains no valid data"));
        } else {
          resolve(validData);
        }
      },
      error: (error) => {
        reject(new Error(`Error parsing CSV: ${error.message}`));
      },
    });
  });
};

export const parseCsvPreview = (file, rows = 10) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: true,
      preview: rows,
      complete: (results) => {
        const data = (results.data || []).filter((row) => {
          return row && Object.values(row).some((v) => v !== null && v !== "");
        });
        resolve(data);
      },
      error: (error) => reject(new Error(`Error parsing CSV preview: ${error.message}`)),
    });
  });
};

export const validatePreview = async (partnerName, file) => {
  if (!partnerName) return { ok: false, data: null };
  const rows = await parseCsvPreview(file, 10);
  if (rows.length === 0) {
    return {
      ok: false,
      data: { failedRecords: [] },
      summary: { totalPreview: 0, failedCount: 0, hasFailures: false, allFailed: false },
    };
  }
  try {
    const data = await validateCSV({ partnerName }, rows);
    const totalPreview = rows.length;
    const failedCount = Array.isArray(data?.failedRecords) ? data.failedRecords.length : 0;
    const hasFailures = failedCount > 0;
    const allFailed = hasFailures && failedCount === totalPreview;
    return {
      ok: !hasFailures,
      data,
      summary: { totalPreview, failedCount, hasFailures, allFailed },
    };
  } catch (e) {
    const totalPreview = rows.length;
    const failedRecords = e?.response?.data?.failedRecords;
    const failedCount = Array.isArray(failedRecords) ? failedRecords.length : totalPreview;
    const hasFailures = failedCount > 0;
    const allFailed = hasFailures && failedCount === totalPreview;
    return {
      ok: !hasFailures,
      data: e?.response?.data || null,
      summary: { totalPreview, failedCount, hasFailures, allFailed },
    };
  }
};

export const uploadToS3 = async (signedUrl, file, onProgress) => {
  try {
    await axios.put(signedUrl, file, {
      headers: {
        "Content-Type": "text/csv",
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        onProgress(progress * 0.85);
      },
    });
  } catch (error) {
    if (error.response?.status === 403 || !error.response) {
      throw new Error("CORS configuration issue. Please contact administrator.");
    }
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const handleFileUpload = async (selectedConfig, csvFile, onProgress) => {
  try {
    const { uploadUrl, key, filename } = await getSignedUrl(selectedConfig, csvFile.name);
    await uploadToS3(uploadUrl, csvFile, onProgress);
    onProgress(90);
    await trackUpload(selectedConfig, filename, key);
    onProgress(100);
  } catch (error) {
    throw new Error(error.message || "Upload failed. Please try again.");
  }
};

export const formatFileSize = (file) => {
  if (!file) return "";
  const sizeInMB = (file.size / 1024 / 1024).toFixed(2);
  return `${sizeInMB} MB`;
};

export const createDragHandlers = (setIsDragging) => ({
  handleDragOver: (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  },

  handleDragLeave: (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  },

  handleFileDrop: (e, onFileSelect) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      onFileSelect(file);
    }
  },
});
